import { useMemo, useState, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { 
  signIn as signInQuery,
  signOut as signOutQuery,
  getCurrentUser,
  getCurrentSession,
  checkDoctorPermissions,
  requestPasswordReset as requestPasswordResetQuery,
  updatePassword as updatePasswordQuery
} from "@/lib/supabase/queries/auth";
import { authKeys, invalidationHelpers } from "@/lib/react-query/keys";
import type { AuthUser, DoctorProfile } from "@/lib/supabase/client";

type LoginResult = { 
  success: boolean; 
  error?: string; 
};

type PasswordResetResult = { 
  success: boolean; 
  error?: string; 
};

type PasswordUpdateResult = { 
  success: boolean; 
  error?: string; 
};

/**
 * Authentication hook using React Query for online-first architecture.
 * Manages sign in, sign out, session management, and user permissions.
 */
export const useAuth = () => {
  const queryClient = useQueryClient();
  
  // Get current user session
  const {
    data: sessionData,
    isLoading: sessionLoading,
    error: sessionError,
    refetch: refetchSession
  } = useQuery({
    queryKey: authKeys.currentUser(),
    queryFn: getCurrentSession,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Get doctor permissions if user is a doctor (including pending)
  const {
    data: permissionsData,
    isLoading: permissionsLoading,
    refetch: refetchPermissions
  } = useQuery({
    queryKey: authKeys.permissions(sessionData?.data?.user?.id || 'unknown'),
    queryFn: checkDoctorPermissions,
    enabled: !!sessionData?.data?.user && (sessionData.data.user.role === 'doctor' || sessionData.data.user.role === 'doctor_pending'),
    retry: 1,
  });

  // Derived state
  const user = sessionData?.data?.user || null;
  const profile = sessionData?.data?.profile || null;
  const session = sessionData?.data?.session || null;
  const isAuthenticated = !!user;
  const loading = sessionLoading || permissionsLoading;
  const error = sessionError || sessionData?.error;
  
  // Doctor-specific permissions
  const isDoctor = user?.role === 'doctor' || user?.role === 'doctor_pending';
  const isVerified = permissionsData?.data?.isVerified || false;
  const canPrescribe = permissionsData?.data?.canPrescribe || false;
  const isSuspended = permissionsData?.data?.isSuspended || false;

  // Sign in mutation
  const signInMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      signInQuery({ email, password }),
    onSuccess: (result) => {
      if (result.data) {
        // Invalidate and refetch user session data
        queryClient.setQueryData(authKeys.currentUser(), result);
        queryClient.invalidateQueries({ queryKey: authKeys.all });
        
        // If user is a doctor (including pending), fetch permissions
        if (result.data.user.role === 'doctor' || result.data.user.role === 'doctor_pending') {
          queryClient.invalidateQueries({
            queryKey: authKeys.permissions(result.data.user.id)
          });
        }
      }
    },
    onError: (error) => {
      // Silent fail - error is handled in the login function
    },
  });

  /**
   * Login with email and password
   */
  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    try {
      const result = await signInMutation.mutateAsync({ email, password });
      
      if (result.error) {
        const message = result.error.message.includes("Invalid login credentials") 
          ? "Invalid email or password"
          : result.error.message;
        return { success: false, error: message };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || "Login failed" };
    }
  }, [signInMutation]);

  // Sign out mutation
  const signOutMutation = useMutation({
    mutationFn: signOutQuery,
    onSuccess: () => {
      // Clear all cached user data
      queryClient.clear();
      // Optionally redirect to login page
    },
    onError: (error) => {
      // Clear cache even if logout fails
      queryClient.clear();
    },
  });

  /**
   * Logout user
   */
  const logout = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const result = await signOutMutation.mutateAsync();
      
      if (result.error) {
        return { success: false, error: result.error.message };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || "Logout failed" };
    }
  }, [signOutMutation]);

  // Password reset mutation
  const passwordResetMutation = useMutation({
    mutationFn: ({ email }: { email: string }) => requestPasswordResetQuery({ email }),
    onError: (error) => {
      // Silent fail - error is handled in the function
    },
  });

  /**
   * Request password reset
   */
  const requestPasswordReset = useCallback(async (email: string): Promise<PasswordResetResult> => {
    try {
      const result = await passwordResetMutation.mutateAsync({ email });
      
      if (result.error) {
        return { success: false, error: result.error.message };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || "Password reset request failed" };
    }
  }, [passwordResetMutation]);

  // Password update mutation
  const passwordUpdateMutation = useMutation({
    mutationFn: ({ password }: { password: string }) => updatePasswordQuery({ password }),
    onError: (error) => {
      // Silent fail - error is handled in the function
    },
  });

  /**
   * Update password
   */
  const updatePassword = useCallback(async (password: string): Promise<PasswordUpdateResult> => {
    try {
      const result = await passwordUpdateMutation.mutateAsync({ password });
      
      if (result.error) {
        return { success: false, error: result.error.message };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || "Password update failed" };
    }
  }, [passwordUpdateMutation]);

  /**
   * Refresh current session and permissions
   */
  const refresh = useCallback(async () => {
    await Promise.all([
      refetchSession(),
      (user?.role === 'doctor' || user?.role === 'doctor_pending') ? refetchPermissions() : Promise.resolve()
    ]);
  }, [refetchSession, refetchPermissions, user?.role]);

  return {
    // User data
    user,
    profile,
    session,
    isAuthenticated,
    
    // Doctor permissions
    isDoctor,
    isVerified,
    canPrescribe,
    isSuspended,
    
    // Loading states
    loading,
    isSigningIn: signInMutation.isPending,
    isSigningOut: signOutMutation.isPending,
    isRequestingPasswordReset: passwordResetMutation.isPending,
    isUpdatingPassword: passwordUpdateMutation.isPending,
    
    // Errors
    error,
    signInError: signInMutation.error,
    signOutError: signOutMutation.error,
    passwordResetError: passwordResetMutation.error,
    passwordUpdateError: passwordUpdateMutation.error,
    
    // Methods
    login,
    logout,
    requestPasswordReset,
    updatePassword,
    refresh
  };
};